import { AuditActionType, type EntityHistoryItem } from "@/api/admin/auditLog";

import {
  type FieldMapping,
  type FieldResolver,
  getChanges,
} from "./getChanges";

export type HistoryTitleResolver = (
  item: EntityHistoryItem
) => string | undefined;

const formatFieldLabelForTitle = (
  field: string,
  fieldLabel: string,
  item: EntityHistoryItem,
  includeEntityNameInTitle: boolean
): string => {
  if (!includeEntityNameInTitle || !item.entityName) {
    return fieldLabel;
  }

  if (field === "displayOrder") {
    return `Dish "${item.entityName}" order`;
  }

  if (field === "order" && item.entityType === "Menu Category") {
    return `Category "${item.entityName}" order`;
  }

  return `${item.entityName}: ${fieldLabel}`;
};

export const getHistoryTitle = (
  item: EntityHistoryItem,
  mapKeyToName?: FieldMapping,
  fieldResolvers?: Record<string, FieldResolver>,
  includeEntityNameInTitle = false,
  resolveHistoryTitle?: HistoryTitleResolver
) => {
  const customTitle = resolveHistoryTitle?.(item);

  if (customTitle !== undefined) {
    return customTitle;
  }

  if (item.action === AuditActionType.CREATE) {
    return `${item.entityName} created`;
  }

  if (item.action === AuditActionType.DELETE) {
    return `${item.entityName} deleted`;
  }

  const changes = getChanges(item, mapKeyToName, fieldResolvers);

  const withEntityName = (title: string) =>
    includeEntityNameInTitle && item.entityName
      ? `${item.entityName}: ${title}`
      : title;

  if (changes.length === 1) {
    const fieldLabel = formatFieldLabelForTitle(
      changes[0].field,
      changes[0].fieldLabel,
      item,
      includeEntityNameInTitle
    );

    return `${fieldLabel} updated`;
  }

  return withEntityName("Details updated");
};
